import {
    Button,
    Callout,
    Dialog,
    DialogBody,
    DialogFooter,
    FormGroup,
    HTMLSelect,
    InputGroup,
    Intent,
    MenuItem,
    NumericInput,
    Position,
    Radio,
    RadioGroup,
    Section,
    SectionCard,
    Toaster,
    Tooltip
} from "@blueprintjs/core";
import { ItemRenderer, Select } from "@blueprintjs/select";
import { segmentTrackCreateSpeculativeProject } from "in_queue/analytics";
import { useCluster } from "in_queue/contexts/ClusterContext";
import { useCanModifyTrackedProjects } from "in_queue/hooks/useCanModifyTrackedProjects";
import { ModelPOI, POIType } from "in_queue/types/modelPOI";
import { SpeculativeProjectComponent } from "in_queue/types/projectType";
import {
    FuelComponentTypes,
    FuelType
} from "in_queue/types/speculativeProjectType";
import { ChangeEvent, FormEvent, useEffect, useRef, useState } from "react";
import { useDebounce } from "use-debounce";
import css from "./CreateSpeculativeProject.module.scss";
import { useCreateSpeculativeProject } from "./useCreateSpeculativeProject";
import { useModelPOIs } from "./useModelPOIs";

const FUEL_TYPE_TO_FUEL_COMPONENTS: Record<FuelType, FuelComponentTypes[]> = {
    [FuelType.SOLAR]: [FuelComponentTypes.SOLAR],
    [FuelType.WIND]: [FuelComponentTypes.WIND],
    [FuelType.STORAGE]: [FuelComponentTypes.STORAGE],
    [FuelType.SOLAR_AND_STORAGE]: [
        FuelComponentTypes.SOLAR,
        FuelComponentTypes.STORAGE
    ],
    [FuelType.WIND_AND_STORAGE]: [
        FuelComponentTypes.WIND,
        FuelComponentTypes.STORAGE
    ]
};

interface FuelComponentLabels {
    title: string;
    mwEnergyLabel: string;
    mwEnergyHelperText: string;
    mwCapacityLabel: string;
    mwCapacityHelperText: string;
}

const MFO_LABELS = {
    title: "MFO Sizing",
    mwEnergyLabel: "MFO MW Energy",
    mwEnergyHelperText: "Maximum Facility Size (Energy)",
    mwCapacityLabel: "MFO MW Capacity",
    mwCapacityHelperText: "Capacity Injection Rights (MW)"
};

const STORAGE_LABELS = {
    title: "Storage Component",
    mwEnergyLabel: "Storage MFO",
    mwEnergyHelperText: "Maximum Facility Output (MW)",
    mwCapacityLabel: "Storage CIR",
    mwCapacityHelperText: "Capacity Injection Rights (MW)"
};

const WIND_LABELS = {
    title: "Wind Component",
    mwEnergyLabel: "Generation MFO",
    mwEnergyHelperText: "Maximum Facility Output (MW)",
    mwCapacityLabel: "Generation CIR",
    mwCapacityHelperText: "Capacity Injection Rights (MW)"
};

const SOLAR_COMPONENT = {
    title: "Solar Component",
    mwEnergyLabel: "Generation MFO",
    mwEnergyHelperText: "Maximum Facility Output (MW)",
    mwCapacityLabel: "Generation CIR",
    mwCapacityHelperText: "Capacity Injection Rights (MW)"
};

const FUEL_TYPE_TO_LABELS: Record<FuelComponentTypes, FuelComponentLabels> = {
    [FuelComponentTypes.SOLAR]: SOLAR_COMPONENT,
    [FuelComponentTypes.WIND]: WIND_LABELS,
    [FuelComponentTypes.STORAGE]: STORAGE_LABELS,
    [FuelComponentTypes.MFO]: MFO_LABELS
};

const FUEL_TYPE_OPTIONS = Object.values(FuelType);
const DEFAULT_FUEL_TYPE = FuelType.SOLAR;

export const CreateSpeculativeProjectButton = () => {
    const canCreateSpeculativeProject = useCanModifyTrackedProjects();
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [isSubmitLoading, setIsSubmitLoading] = useState(false);
    const [validationErrors, setValidationErrors] = useState<string[]>([]);

    const [projectName, setProjectName] = useState<string>("");
    const [fuelType, setFuelType] = useState<FuelType>(DEFAULT_FUEL_TYPE);
    const [projectComponents, setProjectComponents] = useState<
        Record<string, SpeculativeProjectComponent>
    >(() => getInitialProjectComponents(DEFAULT_FUEL_TYPE));
    const [poiType, setPoiType] = useState<POIType>(POIType.POI);
    const [selectedPoi, setSelectedPoi] = useState<ModelPOI | null>(null);

    const [poiQuery, setPOIQuery] = useState("");
    const [debouncedPOIQuery] = useDebounce(poiQuery, 300);
    const modelPOIs = useModelPOIs({ poiQuery: debouncedPOIQuery, poiType });

    const cluster = useCluster();
    const createSpeculativeProject = useCreateSpeculativeProject();

    const validationErrorRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        // If there are validation errors, make sure they are in view
        if (validationErrors.length > 0 && validationErrorRef.current) {
            validationErrorRef.current.scrollIntoView({
                behavior: "smooth",
                block: "nearest"
            });
        }
    }, [validationErrors.length]);

    const handleFuelTypeChange = (event: ChangeEvent<HTMLSelectElement>) => {
        const newFuelType = event.target.value as FuelType;
        setFuelType(newFuelType);

        const newFuelComponents = FUEL_TYPE_TO_FUEL_COMPONENTS[newFuelType];
        const newProjectComponents = getInitialProjectComponents(newFuelType);

        // Add child components for hybrid fuel types
        if (newFuelComponents.length > 1) {
            newFuelComponents.forEach((componentType) => {
                newProjectComponents[componentType] =
                    defaultProjectComponent(componentType);
            });
        }

        setProjectComponents(newProjectComponents);
    };

    const handlePoiTypeChange = (event: FormEvent<HTMLInputElement>) => {
        // if we're changing the POI Type, we need to also clear the selected POI
        const newPoiType = (event.target as HTMLInputElement).value as POIType;

        if (newPoiType !== poiType) {
            setSelectedPoi(null);
            setPoiType((event.target as HTMLInputElement).value as POIType);
        }
    };

    const poiRenderer: ItemRenderer<ModelPOI> = (
        item,
        { handleClick, handleFocus }
    ) => {
        return (
            <MenuItem
                text={item.poiName}
                roleStructure="listoption"
                onClick={handleClick}
                onFocus={handleFocus}
                label={item.poi}
            />
        );
    };

    const closeDialog = () => {
        setIsDialogOpen(false);

        // Reset all the values
        setProjectName("");
        setSelectedPoi(null);
        setProjectComponents(getInitialProjectComponents(fuelType));
        setValidationErrors([]);
        setPOIQuery("");
        setFuelType(DEFAULT_FUEL_TYPE);
    };

    const onSubmit = async () => {
        if (!selectedPoi) {
            return;
        }

        setIsSubmitLoading(true);

        const newValidationErrors =
            validateProjectComponents(projectComponents);
        setValidationErrors(newValidationErrors);
        if (newValidationErrors.length > 0) {
            setIsSubmitLoading(false);
            return;
        }

        const trimmedProjectName = projectName.trim();
        try {
            await createSpeculativeProject({
                projectComponents,
                projectName: trimmedProjectName,
                selectedPOI: selectedPoi
            });

            CreateSpeculativeProjectToaster.show({
                intent: Intent.SUCCESS,
                message: `Successfully created new ${fuelType} speculative project ${trimmedProjectName}`
            });

            segmentTrackCreateSpeculativeProject({
                region: cluster.region,
                studyCycle: cluster.studyCycle,
                studyGroup: cluster.studyGroup,
                studyPhase: cluster.studyPhase
            });

            setIsSubmitLoading(false);
            closeDialog();
        } catch (error) {
            CreateSpeculativeProjectToaster.show({
                intent: Intent.DANGER,
                message: `Unable to create new speculative project`
            });
        }
    };

    const isSubmitDisabled =
        projectName.trim() === "" ||
        !selectedPoi ||
        Object.values(projectComponents).every(
            (pc: SpeculativeProjectComponent) =>
                pc.size.erisMw === 0 && pc.size.nrisMw === 0
        );

    return (
        <div>
            <Tooltip
                content="Unable to create additional study projects due to the project limit"
                position="bottom"
                disabled={canCreateSpeculativeProject}
            >
                <Button
                    intent="primary"
                    icon="plus"
                    onClick={() => setIsDialogOpen(true)}
                    disabled={!canCreateSpeculativeProject}
                >
                    Add study project
                </Button>
            </Tooltip>
            <Dialog
                title="Add Study Project"
                isOpen={isDialogOpen}
                onClose={closeDialog}
                canOutsideClickClose
                className={css["add-speculative-project-dialog"]}
            >
                <DialogBody
                    className={css["add-speculative-project-dialog-body"]}
                >
                    <Section
                        title="Project Name"
                        compact
                        className={css["speculative-project-section"]}
                    >
                        <SectionCard>
                            <FormGroup
                                label="Project Name"
                                labelInfo="(required)"
                            >
                                <InputGroup
                                    placeholder="Insert project name.."
                                    value={projectName}
                                    onValueChange={setProjectName}
                                />
                            </FormGroup>
                        </SectionCard>
                    </Section>
                    <Section
                        title="Project Type"
                        compact
                        className={css["speculative-project-section"]}
                    >
                        <SectionCard>
                            <FormGroup label="Fuel Type" labelInfo="(required)">
                                <HTMLSelect
                                    onChange={(event) =>
                                        handleFuelTypeChange(event)
                                    }
                                    value={fuelType}
                                    options={FUEL_TYPE_OPTIONS}
                                    fill
                                />
                            </FormGroup>
                        </SectionCard>
                    </Section>
                    <Section
                        title="Point of Interconnection (POI)"
                        compact
                        className={css["speculative-project-section"]}
                    >
                        <SectionCard>
                            <RadioGroup
                                label="POI Configuration"
                                onChange={handlePoiTypeChange}
                                selectedValue={poiType}
                            >
                                <Radio
                                    label="Single Bus Number"
                                    value={POIType.POI}
                                />
                                <Radio
                                    label="Facility ID"
                                    value={POIType.LINE_TAP}
                                />
                            </RadioGroup>
                        </SectionCard>
                        <SectionCard>
                            <FormGroup
                                label={
                                    poiType === POIType.POI
                                        ? "Bus Number"
                                        : "Facility ID"
                                }
                                labelInfo="(required)"
                            >
                                <Select<ModelPOI>
                                    items={modelPOIs}
                                    itemRenderer={poiRenderer}
                                    onItemSelect={setSelectedPoi}
                                    query={poiQuery}
                                    onQueryChange={setPOIQuery}
                                    filterable={true}
                                    fill={true}
                                    popoverProps={{
                                        matchTargetWidth: true
                                    }}
                                >
                                    <Button
                                        className={css["pois-select-button"]}
                                        text={
                                            selectedPoi ? (
                                                <div
                                                    className={
                                                        css["selected-poi-text"]
                                                    }
                                                >
                                                    <span>
                                                        {selectedPoi.poiName}
                                                    </span>
                                                    <span
                                                        className={
                                                            css["selected-poi"]
                                                        }
                                                    >
                                                        ({selectedPoi.poi})
                                                    </span>
                                                </div>
                                            ) : (
                                                `Select ${
                                                    poiType === POIType.POI
                                                        ? "bus number"
                                                        : "facility ID"
                                                }
                                                `
                                            )
                                        }
                                        fill={true}
                                        endIcon="double-caret-vertical"
                                    />
                                </Select>
                            </FormGroup>
                        </SectionCard>
                    </Section>
                    {Object.keys(projectComponents).map((pcKey: string) => {
                        const labels =
                            FUEL_TYPE_TO_LABELS[pcKey as FuelComponentTypes];
                        return (
                            <SpeculativeProjectComponentSize
                                key={pcKey}
                                labels={labels}
                                projectComponent={projectComponents[pcKey]}
                                handleProjectComponentChange={(
                                    newValue: SpeculativeProjectComponent
                                ) => {
                                    const newProjectComponents = {
                                        ...projectComponents,
                                        [pcKey]: newValue
                                    };
                                    setValidationErrors([]);
                                    setProjectComponents(newProjectComponents);
                                }}
                            />
                        );
                    })}
                    {validationErrors.length > 0 && (
                        <div ref={validationErrorRef}>
                            <Callout intent="danger">
                                Unable to add new speculative project due to the
                                following validation errors:
                                <ul>
                                    {validationErrors.map(
                                        (error: string, index: number) => (
                                            <li
                                                key={`validation-error-${index}`}
                                            >
                                                {error}
                                            </li>
                                        )
                                    )}
                                </ul>
                            </Callout>
                        </div>
                    )}
                </DialogBody>
                <DialogFooter
                    actions={
                        <div>
                            <Button
                                intent="none"
                                text="Close"
                                onClick={closeDialog}
                            />
                            <Button
                                intent="primary"
                                text="Add project"
                                onClick={onSubmit}
                                disabled={isSubmitDisabled}
                                loading={isSubmitLoading}
                            />
                        </div>
                    }
                />
            </Dialog>
        </div>
    );
};

const SpeculativeProjectComponentSize = ({
    projectComponent,
    handleProjectComponentChange,
    labels
}: {
    projectComponent: SpeculativeProjectComponent;
    handleProjectComponentChange: (
        newProjectComponent: SpeculativeProjectComponent
    ) => void;
    labels: FuelComponentLabels;
}) => {
    return (
        <Section
            title={labels.title}
            className={css["speculative-project-section"]}
        >
            <SectionCard
                className={css["project-component-sizes-section-card"]}
            >
                <FormGroup
                    label={labels.mwEnergyLabel}
                    helperText={labels.mwEnergyHelperText}
                    className={css["project-size-numeric-input"]}
                    labelFor={labels.mwEnergyLabel}
                >
                    <NumericInput
                        id={labels.mwEnergyLabel}
                        onValueChange={(_, newValue) => {
                            const newSize = parseFloat(newValue || "0.0");
                            handleProjectComponentChange({
                                ...projectComponent,
                                size: {
                                    ...projectComponent.size,
                                    erisMw: newSize
                                },
                                mfoMw: newSize
                            });
                        }}
                        min={0.0}
                        max={9999.0}
                        stepSize={10.0}
                        value={projectComponent.size.erisMw}
                        fill={true}
                    />
                </FormGroup>
                <FormGroup
                    label={labels.mwCapacityLabel}
                    helperText={labels.mwCapacityHelperText}
                    className={css["project-size-numeric-input"]}
                    labelFor={labels.mwCapacityLabel}
                >
                    <NumericInput
                        id={labels.mwCapacityLabel}
                        onValueChange={(_, newValue) => {
                            const newSize = parseFloat(newValue || "0.0");
                            handleProjectComponentChange({
                                ...projectComponent,
                                size: {
                                    ...projectComponent.size,
                                    nrisMw: newSize
                                }
                            });
                        }}
                        min={0.0}
                        max={9999.0}
                        stepSize={10.0}
                        value={projectComponent.size.nrisMw}
                        fill={true}
                    />
                </FormGroup>
            </SectionCard>
        </Section>
    );
};

const validateProjectComponents = (
    projectComponents: Record<string, SpeculativeProjectComponent>
): string[] => {
    const newValidationErrors: string[] = [];

    let sumCIRComponents = 0;
    Object.entries(projectComponents).forEach(([key, pc]) => {
        const cir = pc.size.nrisMw;
        const mfo = pc.size.erisMw;

        // CIR must not be greater than MFO
        if (cir > mfo) {
            newValidationErrors.push(
                `CIR of ${key} (${cir}MW) must not be greater than the MFO (${mfo}MW).`
            );
        }

        // The sum CIR MW of all the hybrid components must not be greater than the CIR of the MFO
        if (key !== FuelComponentTypes.MFO) {
            sumCIRComponents += cir;
        }
    });

    const parentCIR = projectComponents[FuelComponentTypes.MFO].size.nrisMw;
    const isHybridProject = Object.keys(projectComponents).length > 1;
    if (isHybridProject && sumCIRComponents !== parentCIR) {
        newValidationErrors.push(
            `The CIR sum of all hybrid components (${sumCIRComponents}MW) must be equal to the MFO MW Capacity (${parentCIR}MW).`
        );
    }

    return newValidationErrors;
};

const defaultProjectComponent = (fuelType: string) => ({
    fuelType: fuelType,
    size: {
        erisMw: 0.0,
        nrisMw: 0.0
    },
    mfoMw: 0.0
});

const getInitialProjectComponents = (
    fuelType: FuelType
): Record<string, SpeculativeProjectComponent> => ({
    [FuelComponentTypes.MFO]: defaultProjectComponent(fuelType)
});

const CreateSpeculativeProjectToaster = Toaster.create({
    position: Position.TOP
});
